import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { contactService } from '../../services/contactService';
import { Contact } from '../../types/contact';
import { Conversation } from '../../types/conversation';
import { CONVERSATION_STATUS_LABELS } from '../../types/atendimentoEnums';
import { useToast } from '../../components/UI/ToastProvider';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';

export const ContactDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();
  const [contact, setContact] = useState<Contact | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phoneNumber: '', identifier: '', blocked: false });

  useEffect(() => {
    if (!id)
    {
      return;
    }

    const load = async () => {
      setLoading(true);
      try
      {
        const [contactData, conversationsData] = await Promise.all([
          contactService.getById(id),
          contactService.getConversations(id),
        ]);
        setContact(contactData);
        setConversations(conversationsData);
        setForm({
          name: contactData.name,
          email: contactData.email ?? '',
          phoneNumber: contactData.phoneNumber ?? '',
          identifier: contactData.identifier ?? '',
          blocked: contactData.blocked,
        });
      }
      catch
      {
        addToast('Erro ao carregar contato', 'error');
      }
      finally
      {
        setLoading(false);
      }
    };

    void load();
  }, [id, addToast]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id)
    {
      return;
    }

    setSaving(true);
    try
    {
      const updated = await contactService.update(id, {
        name: form.name.trim(),
        email: form.email || undefined,
        phoneNumber: form.phoneNumber || undefined,
        identifier: form.identifier || undefined,
        blocked: form.blocked,
      });
      setContact(updated);
      addToast('Contato atualizado', 'success');
    }
    catch
    {
      addToast('Erro ao salvar contato', 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  if (loading)
  {
    return (
      <div className="flex justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  if (!contact)
  {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Contato não encontrado.</p>
        <Link to={helpdeskRoutes.contacts}><Button variant="secondary">Voltar</Button></Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow p-6">
        <Link to={helpdeskRoutes.contacts} className="text-sm text-blue-600 hover:underline mb-4 inline-block">
          ← Voltar para contatos
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{contact.name}</h1>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
            <input type="text" value={form.phoneNumber} onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Identificador</label>
            <input type="text" value={form.identifier} onChange={e => setForm(f => ({ ...f, identifier: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.blocked} onChange={e => setForm(f => ({ ...f, blocked: e.target.checked }))} />
            Bloqueado
          </label>
          <Button type="submit" variant="primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Conversas</h2>
        {conversations.length === 0 ? (
          <p className="text-gray-500 text-sm">Nenhuma conversa registrada.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {conversations.map(conv => (
              <li key={conv.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">#{conv.displayId} — {conv.inboxName}</p>
                  <p className="text-xs text-gray-500">
                    {CONVERSATION_STATUS_LABELS[conv.status]} · {new Date(conv.lastActivityAt).toLocaleString('pt-BR')}
                  </p>
                </div>
                <Link to={helpdeskRoutes.conversation(conv.id)}>
                  <Button variant="secondary" size="sm">Abrir</Button>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
